import { memo } from "react";
import { Button, Text } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import { MathJaxHtml } from "../../../../../components/mathJax";
import type { FlashCardUI } from "../models/FlashcardUI";
import { useFlashcardCardUIStateWeb } from "../hooks/useFlashcardCardUIStateWeb";
import { FlashcardFooterWeb } from "./FlashcardFooterWeb";
import styles from "./FlashcardCardBody.module.scss";

type FlashcardCardBodyProps = {
  card: FlashCardUI;
  onBookmark: () => void;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalCards: number;
  onLike: () => void;
  onDislike: () => void;
  questionIds: string[];
  onImagePreviewOpen: (uri: string) => void;
  isNarrowLayout?: boolean;
};

export const FlashcardCardBody = memo(function FlashcardCardBody({
  card,
  onBookmark,
  onNext,
  onPrevious,
  currentIndex,
  totalCards,
  onLike,
  onDislike,
  questionIds,
  onImagePreviewOpen,
  isNarrowLayout,
}: FlashcardCardBodyProps) {
  const { theme } = useTheme();
  const { t } = useTranslation("selflearn");
  const { scrollRef, isDescriptionExpanded, onToggleDescription } =
    useFlashcardCardUIStateWeb();

  const hasExpandableText = Boolean(card.summary || card.description);
  const collapsible = Boolean(isNarrowLayout);
  const scrollCollapsed = collapsible && !isDescriptionExpanded;

  return (
    <div className={styles.container}>
      <div
        ref={scrollRef}
        className={`${styles.scroll} ${scrollCollapsed ? styles.scrollCollapsed : ""}`}
      >
        <div className={styles.scrollContent}>
          {card.titleMedia ? (
            <button
              type="button"
              className={styles.mediaBtn}
              onClick={() => onImagePreviewOpen(card.titleMedia!)}
              aria-label={t("flashcards.openImagePreview")}
            >
              <img className={styles.mediaImg} src={card.titleMedia} alt="" />
            </button>
          ) : null}

          {card.title ? (
            <Text className={styles.title}>{card.title}</Text>
          ) : null}

          {card.summary ? (
            <MathJaxHtml content={card.summary} className={styles.mathBlock} />
          ) : null}

          {card.description ? (
            <MathJaxHtml
              content={card.description}
              className={styles.mathBlock}
            />
          ) : null}
        </div>
      </div>

      {collapsible && hasExpandableText ? (
        <div className={styles.viewMoreWrap}>
          <div className={styles.viewMoreCenter}>
            <Button
              onPress={onToggleDescription}
              shape="circle"
              height={36}
              backgroundColor={theme.colors.primary[30]}
              textColor={theme.colors.primary[99]}
            >
              <Text className={styles.viewMoreText}>
                {isDescriptionExpanded
                  ? t("flashcards.viewLess")
                  : t("flashcards.viewMore")}
              </Text>
            </Button>
          </div>
        </div>
      ) : null}

      <div className={styles.footerWrap}>
        <FlashcardFooterWeb
          card={card}
          onBookmark={onBookmark}
          onNext={onNext}
          onPrevious={onPrevious}
          currentIndex={currentIndex}
          totalCards={totalCards}
          onLike={onLike}
          onDislike={onDislike}
          questionIds={questionIds}
          isNarrowLayout={isNarrowLayout}
        />
      </div>
    </div>
  );
});
